import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, FlaskConical, HeartPulse } from 'lucide-react';

export const LandingScreen = ({ onStart }) => {
  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="relative min-h-screen w-full overflow-hidden bg-brand-bg"
      data-testid="landing-screen"
    >
      {/* ambient color washes */}
      <div
        className="pointer-events-none absolute -top-40 -right-40 h-[420px] w-[420px] rounded-full blur-3xl opacity-30"
        style={{ background: 'radial-gradient(circle at center, #D8E5C0 0%, rgba(216,229,192,0) 70%)' }}
      />
      <div
        className="pointer-events-none absolute -bottom-48 -left-32 h-[380px] w-[380px] rounded-full blur-3xl opacity-40"
        style={{ background: 'radial-gradient(circle at center, #F6F5DF 0%, rgba(246,245,223,0) 70%)' }}
      />

      {/* Top bar */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-8 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="brand-mark">
          <img src="/brand/young-again-logo.png" alt="Young Again" className="h-14 sm:h-20 md:h-28 w-auto" />
        </div>
        <div className="hidden md:flex items-center gap-6 text-sm text-brand-muted font-body">
          <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Physician-formulated</span>
          <span className="flex items-center gap-2"><FlaskConical className="w-4 h-4" /> Science-backed</span>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10 pt-8 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left: text */}
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-border bg-white/70 backdrop-blur-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-forest animate-pulse" />
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-forest font-heading">
              The Science Of Longevity · 8-Question Assessment
            </span>
          </div>

          <h1
            className="mt-6 font-heading font-bold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight text-brand-forest leading-[1.02]"
            data-testid="landing-headline"
          >
            Could your energy decline be starting{' '}
            <span className="relative inline-block">
              <span className="relative z-10 text-brand-moss">inside your cells</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-brand-sage -z-0 rounded" aria-hidden />
            </span>
            ?
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-brand-muted max-w-xl leading-relaxed font-body">
            Take this short, science-backed assessment to uncover what may be quietly slowing your energy after 40 — and learn what your body might actually be asking for.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <button
              onClick={onStart}
              className="group inline-flex items-center justify-center gap-3 bg-brand-forest text-white hover:bg-brand-forestDark transition-all rounded-xl px-6 sm:px-8 py-4 text-sm sm:text-lg font-heading font-semibold uppercase tracking-wider shadow-sm hover:shadow-lg w-full sm:w-auto"
              data-testid="start-quiz-btn"
            >
              Start The 60-Second Assessment
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </button>
            <span className="text-xs sm:text-sm text-brand-muted font-body text-center sm:text-left">Free · 8 questions · ~60 seconds</span>
          </div>

          <div className="mt-12 flex flex-wrap gap-x-8 gap-y-4 items-center text-sm text-brand-muted font-body">
            <div className="flex items-center gap-2" data-testid="trust-indicator-1">
              <HeartPulse className="w-5 h-5 text-brand-moss" />
              <span>Heart & mitochondrial focus</span>
            </div>
            <div className="hidden sm:block h-4 w-px bg-brand-border" />
            <div className="flex items-center gap-2" data-testid="trust-indicator-2">
              <ShieldCheck className="w-5 h-5 text-brand-moss" />
              <span>30+ years in business</span>
            </div>
            <div className="hidden sm:block h-4 w-px bg-brand-border" />
            <div className="flex items-center gap-2" data-testid="trust-indicator-3">
              <FlaskConical className="w-5 h-5 text-brand-moss" />
              <span>Reviewed by wellness formulators</span>
            </div>
          </div>
        </div>

        {/* Right: hero visual — brand-aligned CSS illustration */}
        <div className="lg:col-span-5 relative">
          <div className="relative w-full max-w-md mx-auto aspect-[4/5]">
            <div
              className="absolute inset-0 rounded-[2rem] overflow-hidden border border-white shadow-2xl shadow-brand-forest/20"
              style={{
                background: 'linear-gradient(160deg, #D8E5C0 0%, #F6F5DF 45%, #FFFFFF 100%)',
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="absolute h-[88%] w-[88%] rounded-full border border-brand-moss/15" />
                <div className="absolute h-[68%] w-[68%] rounded-full border border-brand-moss/20" />
                <div className="absolute h-[46%] w-[46%] rounded-full border border-brand-moss/25" />
                <div className="absolute h-[26%] w-[26%] rounded-full bg-brand-forest flex items-center justify-center shadow-2xl shadow-brand-forest/40">
                  <img
                    src="/brand/young-again-logo.png"
                    alt=""
                    className="h-12 sm:h-14 md:h-16 w-12 sm:w-14 md:w-16 object-contain"
                    style={{ filter: 'invert(94%) sepia(8%) saturate(458%) hue-rotate(40deg) brightness(98%) contrast(94%)' }}
                  />
                </div>
              </div>

              <div className="absolute top-4 sm:top-6 left-4 sm:left-6 text-[9px] sm:text-[10px] uppercase tracking-[0.22em] sm:tracking-[0.25em] font-semibold text-brand-moss font-heading">CoQ10</div>
              <div className="absolute top-4 sm:top-6 right-4 sm:right-6 text-[9px] sm:text-[10px] uppercase tracking-[0.22em] sm:tracking-[0.25em] font-semibold text-brand-moss font-heading">Mitochondria</div>
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 text-[9px] sm:text-[10px] uppercase tracking-[0.22em] sm:tracking-[0.25em] font-semibold text-brand-moss font-heading">Vitality</div>
              <div className="absolute bottom-4 sm:bottom-6 right-4 sm:right-6 text-[9px] sm:text-[10px] uppercase tracking-[0.22em] sm:tracking-[0.25em] font-semibold text-brand-moss font-heading">Heart</div>

              <div className="absolute top-1/3 left-8 h-2 w-2 rounded-full bg-brand-forest animate-pulse" />
              <div className="absolute top-2/3 right-10 h-1.5 w-1.5 rounded-full bg-brand-moss animate-pulse" style={{ animationDelay: '1s' }} />
              <div className="absolute top-1/4 right-1/4 h-1 w-1 rounded-full bg-brand-forest animate-pulse" style={{ animationDelay: '2s' }} />
            </div>

            {/* floating stat card */}
            <div
              className="absolute left-2 sm:-left-4 md:-left-10 bottom-6 sm:bottom-10 bg-white rounded-2xl shadow-xl shadow-brand-forest/15 px-4 sm:px-5 py-3 sm:py-4 border border-brand-border max-w-[200px] sm:max-w-[230px]"
              data-testid="floating-stat-card"
            >
              <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.22em] font-semibold text-brand-muted font-heading">Cellular Energy</div>
              <div className="mt-1 font-heading font-bold text-lg sm:text-xl text-brand-forest leading-tight">
                Declines up to <span className="text-brand-moss">50%</span> by age 65
              </div>
              <div className="mt-1.5 sm:mt-2 text-[11px] sm:text-xs text-brand-muted font-body">— Published research on CoQ10 levels</div>
            </div>

            {/* floating top-right tag */}
            <div className="absolute right-2 sm:-right-4 md:-right-8 top-6 sm:top-12 bg-brand-forest text-white rounded-2xl shadow-xl shadow-brand-forest/30 px-3 sm:px-4 py-2.5 sm:py-3 max-w-[150px] sm:max-w-[180px]">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.22em] font-semibold text-brand-sage font-heading">Spark</div>
              <div className="mt-0.5 font-heading font-semibold text-sm sm:text-base leading-tight">One critical compound</div>
            </div>
          </div>
        </div>
      </div>

      {/* bottom strip — brand tagline */}
      <div className="relative border-t border-brand-border bg-brand-ivory/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 py-4 sm:py-5 flex items-center justify-between text-[11px] sm:text-xs text-brand-muted font-body">
          <span className="font-heading uppercase tracking-[0.18em] sm:tracking-[0.2em] font-semibold text-brand-forest">Age Well. Live Fully.</span>
          <span className="hidden sm:block">Your answers are private. We don't share your information.</span>
        </div>
      </div>
    </motion.section>
  );
};
